import { BankAccountType, useBankActivity, useWealthOverview, type BankActivity } from '@egw/api'
import { Button, ButtonVariant, PageHeader, RefetchingSpinner } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InfiniteScrollWrapper } from '@egw/layouts/src/components'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router'
import { TransactionItem } from '../../components/transaction-item/transaction-item'
import { PAGE_SIZE } from '../../constants'

export interface ActivityProps {
  alias: number
  title: string
  backLinkUrl: string
}

export const Activity: FC<ActivityProps> = ({ alias, title, backLinkUrl }) => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error } = useWealthOverview()

  const allAccounts = useMemo(
    () => [
      ...(wealthOverviewData?.highYieldChecking?.accounts ?? []),
      ...(wealthOverviewData?.highYieldSavings?.accounts ?? []),
    ],
    [wealthOverviewData],
  )

  const data = allAccounts.find((account) => account.alias === alias)

  const subtitle: string = useMemo(() => {
    if (data?.accountType === BankAccountType.HIGH_YIELD_CHECKING) {
      return t(`bank_account_titles.${data.accountTitle}`)
    }
    if (data?.accountType === BankAccountType.HIGH_YIELD_SAVINGS) {
      return wealthOverviewData?.highYieldSavings?.productName ?? ''
    }
    return ''
  }, [data?.accountTitle, data?.accountType, t, wealthOverviewData?.highYieldSavings?.productName])

  const totalBalanceCurrencyCode = data?.balance.total.currencyCode
  const totalBalanceAmount = data?.balance.total.amount
  const availableBalanceCurrencyCode = data?.balance.available.currencyCode
  const availableBalanceAmount = data?.balance.available.amount
  const apy = data?.apy ? data.apy.toFixed(2) : undefined

  const { data: activityData, isLoading: activityIsLoading, dataUpdatedAt } = useBankActivity({ alias })

  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  const [page, setPage] = useState<number>(1)

  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState<boolean>(false)

  useEffect(() => {
    // If WealthOverview Data has returned and there are no accounts, navigate to the overview page
    if (wealthOverviewData && data === undefined) {
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    }
  }, [data, wealthOverviewData, navigate])

  // Get total pages based on the number of transactions and the page size
  const getTotalPages = useMemo((): number => {
    if (!activityData?.activity) return 0
    return Math.ceil(activityData.activity.length / PAGE_SIZE)
  }, [activityData])

  // Load more transactions
  const onLoadMore = useCallback(() => {
    setLoadingMoreTransactions(true)

    setTimeout(() => {
      setLoadingMoreTransactions(false)
      setPage((currPage) => currPage + 1)
    }, 500)
  }, [])

  // Paginate transactions
  const paginatedTransactions = useMemo((): BankActivity[] => {
    if (!activityData?.activity) return []
    const endIndex = page * PAGE_SIZE
    return activityData.activity.slice(0, endIndex)
  }, [activityData, page])

  // Check if there are more transactions to load
  const hasMoreTransactions = useMemo((): boolean => {
    return page < getTotalPages
  }, [page, getTotalPages])

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {data ? (
        <InfiniteScrollWrapper
          onLoadMore={onLoadMore}
          hasMore={hasMoreTransactions}
          isLoading={loadingMoreTransactions}
        >
          <PageHeader title={title} subtitle={subtitle} backLinkUrl={backLinkUrl} lastRefreshed={lastRefreshed} />
          <div className="egw-container py-0">
            {(data.accountType === BankAccountType.HIGH_YIELD_CHECKING ||
              data.accountType === BankAccountType.HIGH_YIELD_SAVINGS) && (
              <div className="flex flex-col gap-2 mb-10">
                <div className="flex">
                  <h3 className="flex-1 text-xl font-semimbold text-header">
                    {t('activity.checking_savings.total_balance')}
                  </h3>
                  <p>
                    {totalBalanceCurrencyCode ? (
                      <span className="text-2xl font-light text-meta">
                        {getCurrencySymbol(totalBalanceCurrencyCode)}
                      </span>
                    ) : null}
                    <span className="text-2xl font-semibold text-header">
                      {Currency.integerCents.onlyMinus(totalBalanceAmount ? totalBalanceAmount : 0)}
                    </span>
                  </p>
                </div>

                <div className="flex text-base font-light text-meta">
                  <p className="flex-1">{t('activity.checking_savings.available_balance')}</p>
                  <div>
                    {availableBalanceCurrencyCode ? (
                      <span>{getCurrencySymbol(availableBalanceCurrencyCode)}</span>
                    ) : null}
                    <span>{Currency.integerCents.onlyMinus(availableBalanceAmount ? availableBalanceAmount : 0)}</span>
                  </div>
                </div>

                <div className="flex text-base font-light text-meta">
                  <p className="flex-1">{t('activity.checking_savings.apy_label')}</p>
                  {apy ? <p>{t('activity.checking_savings.apy', { apy })}</p> : null}
                </div>
              </div>
            )}
          </div>

          <div className="w-full border-b border-light mb-4" />

          <div className="egw-container pt-3xs">
            {/* Transactions list */}
            {paginatedTransactions.map((transaction) => (
              <TransactionItem
                key={transaction.activityId}
                transaction={transaction}
                onClick={() => {
                  data.accountType === BankAccountType.HIGH_YIELD_CHECKING &&
                    void navigate(
                      WEALTH_ROUTES.CHECKING_ACCOUNT_ACTIVITY_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':transactionId', transaction.activityId.toString()),
                    )
                  // TODO UPDATE W/ SAVINGS
                  data.accountType === BankAccountType.HIGH_YIELD_SAVINGS &&
                    void navigate(
                      WEALTH_ROUTES.SAVINGS_ACCOUNT_ACTIVITY_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':transactionId', transaction.activityId.toString()),
                    )
                }}
              />
            ))}

            {/* No transactions */}
            {!activityIsLoading && (!activityData?.activity || activityData.activity.length === 0) && (
              <div className="bg-bglight flex flex-col items-center mt-8 py-6 px-8 gap-6">
                <p className="text-header text-center text-2xl font-light">{t('activity.no_transactions.title')}</p>
                <p className="text-body text-center">{t('activity.no_transactions.description')}</p>
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={() => {
                    void navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                >
                  {t('activity.no_transactions.transfer')}
                </Button>
              </div>
            )}

            {loadingMoreTransactions ? (
              <div className="w-full">
                <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                  <Skeleton count={5} height="50px" />
                </SkeletonTheme>
              </div>
            ) : null}

            {!hasMoreTransactions && paginatedTransactions.length > 0 && (
              <div className="flex justify-center my-4 typo-primary-xs-regular text-text-meta">
                {t('activity.end_of_transactions')}
              </div>
            )}
          </div>
        </InfiniteScrollWrapper>
      ) : null}

      {error ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('activity.error')}</p>
        </div>
      ) : null}
    </>
  )
}
